<template>
  <v-main>
    <loader :loading="loading"></loader>
    <v-container fluid>
      <div class="breadcrumbs-container">
        <Breadcrumbs />
      </div>
      <!-- Threat Spider Info Banner -->
      <div class="threat-spider-banner mb-6">
        <div class="banner-content">
          <v-icon class="banner-icon">mdi-spider</v-icon>
          <div class="banner-text">
            <h3 class="banner-title">Project Components</h3>
            <p class="banner-description">
              Components and their applicable standards are imported from
              <a
                :href="`http://${serverUrl}:5000`"
                target="_blank"
                rel="noopener noreferrer"
                class="threat-spider-link"
              >
                Threat Spider
              </a>
              - a comprehensive threat modeling and risk management tool.
            </p>
          </div>
        </div>
      </div>
      <div class="flex flex-col space-y-6">
        <Header :text="$tc('Threat Spider Components')" />
        <div class="p-4 rounded shadow bg-secondary-100">
          <div class="flex flex-row text-lg tracking-wide uppercase text-secondary-700">
            {{ $tc('Threat Spider Components') }}
          </div>
          <div>
            <div class="flex flex-row items-center space-x-4">
              <v-select
                v-model="searchField"
                :items="searchFields"
                item-text="text"
                item-value="value"
                :label="$t('search-field')"
                hide-details
                outlined
                dense
                class="mb-4 search-field"
                style="max-width: 200px;"
              ></v-select>
              <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                :label="$t('search')"
                hide-details
                class="mb-4 flex-grow-1"
                outlined
                dense
                @keyup.enter="applySearch"
              />
            </div>

            <v-data-table
              :headers="getHeaders"
              :items="components"
              :search="search"
              :custom-filter="customFilter"
              class="elevation-1 certifai-table"
              :loading="loading"
              :items-per-page="5"
              :footer-props="{
                showFirstLastPage: true,
                firstIcon: 'mdi-arrow-collapse-left',
                lastIcon: 'mdi-arrow-collapse-right',
                prevIcon: 'mdi-arrow-left',
                nextIcon: 'mdi-arrow-right',
                disableItemsPerPage: false,
                itemsPerPageText: $t('Components per page'),
                showCurrentPage: true,
              }"
              must-sort
            >
              <template v-slot:[`item.actions`]="{ item }">
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      class="g-action-btn g-action-info"
                      v-on="on"
                      v-bind="attrs"
                      @click="handleProjectClickThreatSpider(item)"
                    >
                      <v-icon small>mdi-spider</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('Edit in Threat Spider') }}</span>
                </v-tooltip>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      class="g-action-btn g-action-view"
                      v-on="on"
                      v-bind="attrs"
                      @click="showStandards(item)"
                    >
                      <v-icon small>mdi-eye</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('preview') }}</span>
                </v-tooltip>
              </template>
            </v-data-table>

            <!-- Standards Modal -->
            <v-dialog v-model="standardsDialog" max-width="700">
              <v-card>
                <v-card-title class="headline">
                  Component Standards
                  <v-spacer></v-spacer>
                  <v-btn icon @click="standardsDialog = false">
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </v-card-title>

                <v-card-text v-if="loading" class="text-center pa-5">
                  <v-progress-circular
                    indeterminate
                    color="primary"
                    size="64"
                  ></v-progress-circular>
                  <div class="mt-3">Loading standards data...</div>
                </v-card-text>

                <template v-else>
                  <!-- Show when no standards data is available -->
                  <v-card-text
                    v-if="!componentStandards || componentStandards.length === 0"
                    class="text-center pa-5"
                  >
                    <v-icon size="64" color="grey lighten-1">
                      mdi-alert-circle-outline
                    </v-icon>
                    <div class="mt-3 text-subtitle-1 grey--text text--darken-1">
                      No standards data available for this component.
                    </div>
                  </v-card-text>

                  <!-- Show when standards data exists -->
                  <v-card-text v-else>
                    <div
                      v-for="standard in componentStandards"
                      :key="standard.StandardID"
                      class="mb-6 pt-5"
                    >
                      <div class="d-flex align-center mb-3">
                        <h3>{{ standard.StandardName }}</h3>
                        <v-spacer></v-spacer>
                        <v-btn
                          color="primary"
                          small
                          @click="createComplianceAssessment(threatSpiderComponent, standard)"
                        >
                          <v-icon left small>mdi-clipboard-text-outline</v-icon>
                          Create Compliance Assessment
                        </v-btn>
                      </div>
                      <v-data-table
                        :headers="standardHeaders"
                        :items="standard.Requirements"
                        hide-default-footer
                        class="elevation-1 certifai-table"
                        dense
                      ></v-data-table>
                    </div>
                  </v-card-text>
                </template>

                <v-card-actions
                  v-if="!loading && (!componentStandards || componentStandards.length === 0)"
                >
                  <v-spacer></v-spacer>
                  <v-btn color="primary" text @click="standardsDialog = false">
                    Close
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </div>
        </div>
      </div>
    </v-container>
  </v-main>
</template>

<script>
import util from '@/mixins/util';
import Header from '@/components/helpers/Header';
import Loader from '@/components/helpers/Loader.vue';
import EventBus from '@/helpers/event-bus.js';
import ThreatSpiderApi from '../api/threatSpiderApi';
import Breadcrumbs from '@/components/charts/Breadcrumbs.vue';

export default {
  name: 'ThreatSpiderComponents',
  components: {
    Header,
    Loader,
    Breadcrumbs,
  },
  mixins: [util],
  data() {
    return {
      serverUrl: 'localhost',
      loading: false,
      search: '',
      searchField: 'all',
      ID: this.$route.query.projectId,
      components: [],
      standardsDialog: false,
      componentStandards: [],
      threatSpiderComponent: null,
      standardHeaders: [
        {
          text: 'Requirement ID',
          value: 'RequirementID',
          width: '15%',
        },
        {
          text: 'Name',
          value: 'RequirementName',
          width: '25%',
        },
        {
          text: 'Description',
          value: 'RequirementDescription',
          width: '45%',
        },
      ],
    };
  },
  computed: {
    getHeaders() {
      return [
        {
          text: 'ID',
          value: 'ID',
          class: 'text text-subtitle-2 font-weight-black',
          width: '5%',
        },
        {
          text: 'Component Name',
          value: 'ComponentName',
          align: 'center',
          class: 'text text-subtitle-2 font-weight-black',
          width: '5%',
        },
        {
          text: this.$t('Description'),
          value: 'Description',
          align: 'center',
          class: 'text text-subtitle-2 font-weight-black',
          width: '5%',
        },
        {
          text: 'Label',
          value: 'Label',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '5%',
        },
        {
          text: 'Operational Context',
          value: 'OperationalContext',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '5%',
        },
        {
          text: 'Manufacturer',
          value: 'Manufacturer',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '5%',
        },
        {
          text: 'Class',
          value: 'Class',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '5%',
        },
        {
          text: 'ICS Type',
          value: 'ICSType',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '5%',
        },
        {
          text: this.$t('soc.headers.actions'),
          value: 'actions',
          align: 'center',
          class: 'text text-subtitle-2 font-weight-black',
          width: '5%',
        },
      ];
    },
    searchFields() {
      return [
        { text: this.$t('search-field-all'), value: 'all' },
        { text: 'ID', value: 'ID' },
        { text: 'Component Name', value: 'ComponentName' },
        { text: this.$t('Description'), value: 'Description' },
        { text: 'Label', value: 'Label' },
        { text: 'Operational Context', value: 'OperationalContext' },
        { text: 'Manufacturer', value: 'Manufacturer' },
        { text: 'Class', value: 'Class' },
        { text: 'ICS Type', value: 'ICSType' },
      ];
    },
  },
  created() {
    this.fetchServerUrl();
    this.fetchComponents();
  },
  methods: {
    async fetchServerUrl() {
      try {
        const response = await ThreatSpiderApi.getServerUrl();
        this.serverUrl = response.data.serverUrl;
      } catch (error) {
        console.error('Failed to fetch server URL:', error);
      }
    },
    async fetchComponents() {
      try {
        this.loading = true;
        const projectPayload = {
          ID: parseInt(this.$route.query.projectId),
        };
        console.log(JSON.stringify(projectPayload));
        const response = await ThreatSpiderApi.getProjectComponentsFromThreatSpider(projectPayload);
        this.components = response.data;
      } catch (error) {
        EventBus.$emit('snackbar', {
          text: this.$t('errorlist.something-went-wrong'),
          color: 'error',
        });
      } finally {
        this.loading = false;
      }
    },
    async showStandards(item) {
      try {
        this.loading = true;
        this.standardsDialog = true; // Open the dialog immediately
        this.threatSpiderComponent = item; // Store the selected component
        const response = await ThreatSpiderApi.getComponentStandardsFromThreatSpider([item.ID]);
        this.componentStandards = response.data;
      } catch (error) {
        this.componentStandards = []; // Set to empty array on error
        EventBus.$emit('snackbar', {
          text: this.$t('errorlist.something-went-wrong'),
          color: 'error',
        });
      } finally {
        this.loading = false;
      }
    },
    handleProjectClickThreatSpider(item) {
      // Open external URL in a new tab
      console.log(item);
      //window.open(`http://${this.serverUrl}:5000/all_tmp_components/${this.ID}`, '_blank');
      window.open(`http://${this.serverUrl}:5000/edit_forms/${this.ID}/all_components`, '_blank');
    },
    createComplianceAssessment(component, standard) {
      this.$router.push({
        name: 'SubjectOfCertification',
        query: {
          component: JSON.stringify(component),
          standard: JSON.stringify(standard),
        },
      });
    },
    customFilter(value, search, item) {
      if (!search) return true;

      search = search.toString().toLowerCase();

      if (this.searchField === 'all') {
        // Search in all fields (default behavior)
        return Object.keys(item).some(key => {
          // Skip action column and non-string values
          if (key === 'actions' || key === 'raw' || item[key] === null || item[key] === undefined) {
            return false;
          }

          try {
            const itemValue = item[key].toString().toLowerCase();
            return itemValue.includes(search);
          } catch (e) {
            return false;
          }
        });
      } else {
        // Search only in the selected field
        if (item[this.searchField] !== null && item[this.searchField] !== undefined) {
          try {
            const itemValue = item[this.searchField].toString().toLowerCase();
            return itemValue.includes(search);
          } catch (e) {
            return false;
          }
        }
        return false;
      }
    },
    applySearch() {
      // This method can be used for any additional search logic if needed
    },
  },
};
</script>

<style>
.breadcrumbs-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 20px;
  padding-bottom: 40px;
}

.breadcrumbs-container .v-breadcrumbs {
  background-color: #ffffff;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 800;
}
.threat-spider-banner {
  background: linear-gradient(135deg, #cc5522 0%, #b86f11 50%, #cc9900 100%);
  border-radius: 16px;
  padding: 0;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(102, 51, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.threat-spider-banner:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 48px rgba(102, 51, 0, 0.4);
}

.banner-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  text-align: center;
}

.banner-icon {
  font-size: 40px !important;
  color: white !important;
  flex-shrink: 0;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.25));
}

.banner-text {
  flex: 1;
}

.banner-title {
  color: white;
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0 0 6px 0;
  letter-spacing: -0.5px;
}

.banner-description {
  color: rgba(255, 255, 255, 0.98);
  font-size: 0.9rem;
  margin: 0;
  line-height: 1.4;
  font-weight: 400;
}

.threat-spider-link {
  color: white;
  text-decoration: none;
  font-weight: 700;
  background: rgba(255, 255, 255, 0.35);
  padding: 4px 10px;
  border-radius: 8px;
  transition: all 0.3s ease;
  display: inline-block;
  border: 1px solid rgba(255, 255, 255, 0.5);
}

.threat-spider-link:hover {
  background: rgba(255, 255, 255, 0.5);
  text-decoration: none;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.7);
}
.search-field {
  min-width: 150px;
}
</style>
